import type {
    CompanyAnalysisTechnologyItem,
    CompanyAnalysisTechnologyOption,
} from "app/pages/sales-intelligence/modules/technology/types/company-analysis-technology-filter";
import { GRID_UPGRADE_MOCK_VALUE } from "app/pages/sales-intelligence/constants/common";

type TechnologiesService = {
    sortTechnologies: (technologies: CompanyAnalysisTechnologyItem[]) => CompanyAnalysisTechnologyItem[];
    convertTechnologiesToOptions: (technologies: CompanyAnalysisTechnologyItem[]) => CompanyAnalysisTechnologyOption[];
    removeDuplicateTechnologies: (technologies: CompanyAnalysisTechnologyItem[]) => CompanyAnalysisTechnologyItem[];
};

export default function createCompanyAnalysisTechnologiesService(): TechnologiesService {
    const sortTechnologies = (technologies: CompanyAnalysisTechnologyItem[]) => {
        return technologies.sort((a, b) => {
            if (a.category < b.category) {
                return -1;
            }
            if (a.category > b.category) {
                return 1;
            }

            if (a.subCategory < b.subCategory) {
                return -1;
            }
            if (a.subCategory > b.subCategory) {
                return 1;
            }

            if (a.technologyName < b.technologyName) {
                return -1;
            }
            if (a.technologyName > b.technologyName) {
                return 1;
            }

            return 0;
        });
    };

    const removeDuplicateTechnologies = (
        technologies: CompanyAnalysisTechnologyItem[],
    ): CompanyAnalysisTechnologyItem[] => {
        const uniqueIds = new Set<string>();

        return technologies.filter((tech) => {
            if (!uniqueIds.has(tech.technologyId)) {
                uniqueIds.add(tech.technologyId);
                return true;
            }
            return false;
        });
    };

    const createOption = (category: string, subCategory?: string) => {
        const isChild = !!subCategory;
        return {
            id: isChild ? `${subCategory}_child` : `${category}_parent`,
            text: isChild ? subCategory : category,
            isChild,
            items: [],
            count: 0,
            parent: category,
        };
    };

    const convertTechnologiesToOptions = (
        technologies: CompanyAnalysisTechnologyItem[],
    ): CompanyAnalysisTechnologyOption[] => {
        const optionsMap = technologies.reduce((map, tech) => {
            const { category, subCategory } = tech;

            if (category === GRID_UPGRADE_MOCK_VALUE || subCategory === GRID_UPGRADE_MOCK_VALUE) {
                return map;
            }

            const categoryKey = `${category.toLowerCase()}_parent`;
            const subCategoryKey = `${subCategory.toLowerCase()}_child`;

            if (!map.has(categoryKey)) {
                map.set(categoryKey, createOption(category));
            }
            (map.get(categoryKey) as CompanyAnalysisTechnologyOption).count++;

            if (!map.has(subCategoryKey)) {
                map.set(subCategoryKey, createOption(category, subCategory));
            }
            map.get(subCategoryKey)!.count++;
            map.get(categoryKey)!.items.push(subCategory);

            return map;
        }, new Map<string, CompanyAnalysisTechnologyOption>());

        const optionsArray = Array.from(optionsMap.values()).map((option) => ({
            ...option,
            items: [...new Set(option.items)],
        }));

        return optionsArray;
    };

    return { sortTechnologies, removeDuplicateTechnologies, convertTechnologiesToOptions };
}
